<template>
  <ManagerLayout>
    <div class="manager-learners">
      <h2 class="page-title">学習者一覧</h2>
      <p class="page-desc">名前・保護者・current_day・bypass で進捗を確認できます。学習者単位で bypass の付与・解除ができます。</p>

      <!-- 検索 -->
      <section class="manager-section filters-section">
        <div class="card">
          <h3 class="section-title">検索</h3>
          <div class="filters">
            <label class="filter-item filter-search">
              <span class="filter-label">名前・ふりがな</span>
              <input
                v-model="searchQ"
                type="text"
                placeholder="部分一致"
                class="form-input"
                @keyup.enter="loadLearners"
              />
            </label>
            <button type="button" class="btn btn-primary" :disabled="loading" @click="loadLearners">
              {{ loading ? '読み込み中...' : '検索' }}
            </button>
            <label class="filter-item">
              <span class="filter-label">新規登録者のデフォルト配信パターン</span>
              <select
                v-model="defaultChannel"
                class="form-input"
                :disabled="loadingDefaultChannel || savingDefaultChannel"
                @change="submitDefaultChannelChange"
              >
                <option value="kyoiku_iinkai">教育委員会</option>
                <option value="net_ad">ネット広告</option>
              </select>
            </label>
          </div>
        </div>
      </section>

      <!-- 一覧 -->
      <section class="manager-section">
        <div class="card">
          <div class="toolbar">
            <span class="total-note">全 {{ total }} 件</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="loading" @click="loadLearners">
              更新
            </button>
          </div>
          <div v-if="loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!learners.length" class="empty-state">該当する学習者がいません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="学習者一覧">
              <thead>
                <tr>
                  <th>名前</th>
                  <th>ふりがな</th>
                  <th>保護者（氏名）</th>
                  <th>保護者（メール）</th>
                  <th class="num">current_day</th>
                  <th>bypass</th>
                  <th>配信パターン</th>
                  <th class="action">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="l in learners" :key="l.id" class="data-row">
                  <td>{{ l.name || '—' }}</td>
                  <td>{{ l.nameKana || '—' }}</td>
                  <td>{{ l.parentName || '—' }}</td>
                  <td>{{ l.parentEmail || '—' }}</td>
                  <td class="num">{{ l.currentDay }}</td>
                  <td>
                    <span v-if="l.bypassSubscription" class="bypass-badge">あり</span>
                    <span v-else>—</span>
                  </td>
                  <td>
                    <span v-if="l.isSelfRegistration" class="channel-badge channel-badge--self">大人（自動）</span>
                    <span v-else-if="l.channel === 'net_ad'" class="channel-badge channel-badge--net-ad">ネット広告</span>
                    <span v-else-if="l.channel === 'kyoiku_iinkai'" class="channel-badge channel-badge--kyoiku">教育委員会</span>
                    <span v-else class="channel-badge channel-badge--default">未指定（既定値）</span>
                  </td>
                  <td class="action">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline"
                      :disabled="updatingNameId === l.id"
                      @click="openNameModal(l)"
                    >
                      {{ updatingNameId === l.id ? '更新中...' : '名前を変更' }}
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline"
                      :disabled="updatingLearnerId === l.id"
                      @click="openDayModal(l)"
                    >
                      {{ updatingLearnerId === l.id ? '更新中...' : 'Dayを変更' }}
                    </button>
                    <button
                      v-if="!l.isSelfRegistration"
                      type="button"
                      class="btn btn-sm btn-outline"
                      :disabled="updatingChannelId === l.id"
                      @click="openChannelModal(l)"
                    >
                      {{ updatingChannelId === l.id ? '更新中...' : '配信パターンを変更' }}
                    </button>
                    <template v-if="l.bypassSubscription">
                      <button
                        type="button"
                        class="btn btn-sm btn-danger"
                        :disabled="bypassRevokingId === l.id"
                        @click="revokeBypassLearner(l)"
                      >
                        {{ bypassRevokingId === l.id ? '処理中...' : 'bypass解除' }}
                      </button>
                    </template>
                    <template v-else>
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        :disabled="bypassGrantingId === l.id"
                        @click="grantBypassLearner(l)"
                      >
                        {{ bypassGrantingId === l.id ? '処理中...' : 'bypass付与' }}
                      </button>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="learners.length > 0 && learners.length < total" class="load-more">
            <button type="button" class="btn btn-secondary" :disabled="loadingMore" @click="loadMore">
              {{ loadingMore ? '読み込み中...' : 'さらに読み込む' }}
            </button>
          </div>
        </div>
      </section>

      <!-- Day変更モーダル -->
      <div v-if="dayModalLearner" class="modal-overlay" @click.self="closeDayModal">
        <div class="modal-card">
          <h3 class="modal-title">current_day を変更</h3>
          <p class="modal-desc">
            {{ dayModalLearner.name || '—' }}（{{ dayModalLearner.parentEmail || '—' }}）の Day を変更します。
          </p>
          <div class="modal-form">
            <label class="form-label">
              <span>新しい Day（1 以上の整数）</span>
              <input
                v-model.number="dayModalValue"
                type="number"
                min="1"
                step="1"
                class="form-input num-input"
                @keyup.enter="submitDayChange('learner')"
              />
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeDayModal">キャンセル</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!isValidDay(dayModalValue) || savingDay"
              @click="submitDayChange('learner')"
            >
              {{ savingDay ? '保存中...' : '変更する' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 名前変更モーダル -->
      <div v-if="nameModalLearner" class="modal-overlay" @click.self="closeNameModal">
        <div class="modal-card">
          <h3 class="modal-title">名前を変更</h3>
          <p class="modal-desc">
            {{ nameModalLearner.name || '—' }}（{{ nameModalLearner.parentEmail || '—' }}）の名前を変更します。
          </p>
          <div class="modal-form">
            <label class="form-label">
              <span>新しい名前</span>
              <input
                v-model.trim="nameModalValue"
                type="text"
                class="form-input"
                @keyup.enter="submitNameChange"
              />
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeNameModal">キャンセル</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!nameModalValue.trim() || savingName"
              @click="submitNameChange"
            >
              {{ savingName ? '保存中...' : '変更する' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 配信パターン変更モーダル -->
      <div v-if="channelModalLearner" class="modal-overlay" @click.self="closeChannelModal">
        <div class="modal-card">
          <h3 class="modal-title">配信パターンを変更</h3>
          <p class="modal-desc">
            {{ channelModalLearner.name || '—' }}（{{ channelModalLearner.parentEmail || '—' }}）のLINE配信パターンを変更します。
            Day13以降のLINE文面（Day13/Day14/完了報告・完走後の案内）に反映されます。
          </p>
          <div class="modal-form">
            <label class="form-label">
              <span>配信パターン</span>
              <select v-model="channelModalValue" class="form-input">
                <option :value="null">未指定（その時点のデフォルトに従う）</option>
                <option value="kyoiku_iinkai">教育委員会</option>
                <option value="net_ad">ネット広告</option>
              </select>
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeChannelModal">キャンセル</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="savingChannel"
              @click="submitChannelChange"
            >
              {{ savingChannel ? '保存中...' : '変更する' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''
const PAGE_SIZE = 500

const searchQ = ref('')
const learners = ref([])
const total = ref(0)
const loading = ref(false)
const loadingMore = ref(false)
const currentOffset = ref(0)
const updatingLearnerId = ref(null)
const dayModalLearner = ref(null)
const dayModalValue = ref(1)
const savingDay = ref(false)
const bypassGrantingId = ref(null)
const bypassRevokingId = ref(null)
const nameModalLearner = ref(null)
const nameModalValue = ref('')
const savingName = ref(false)
const updatingNameId = ref(null)
const channelModalLearner = ref(null)
const channelModalValue = ref(null)
const savingChannel = ref(false)
const updatingChannelId = ref(null)
const defaultChannel = ref('kyoiku_iinkai')
const loadingDefaultChannel = ref(false)
const savingDefaultChannel = ref(false)

function isValidDay (v) {
  const n = typeof v === 'number' ? v : parseInt(v, 10)
  return Number.isInteger(n) && n >= 1
}

function openDayModal (l) {
  dayModalLearner.value = l
  dayModalValue.value = l.currentDay ?? 1
}

function closeDayModal () {
  dayModalLearner.value = null
  dayModalValue.value = 1
}

async function submitDayChange (target) {
  if (target !== 'learner' || !dayModalLearner.value) return
  const learnerId = dayModalLearner.value.id
  const currentDay = typeof dayModalValue.value === 'number' ? dayModalValue.value : parseInt(dayModalValue.value, 10)
  if (!isValidDay(currentDay)) return
  savingDay.value = true
  updatingLearnerId.value = learnerId
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/learners/${encodeURIComponent(learnerId)}/current-day`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ currentDay })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || '更新に失敗しました')
      return
    }
    const idx = learners.value.findIndex((l) => l.id === learnerId)
    if (idx !== -1) learners.value[idx].currentDay = data.currentDay
    closeDayModal()
  } finally {
    savingDay.value = false
    updatingLearnerId.value = null
  }
}

function openNameModal (l) {
  nameModalLearner.value = l
  nameModalValue.value = l.name || ''
}

function closeNameModal () {
  nameModalLearner.value = null
  nameModalValue.value = ''
}

async function submitNameChange () {
  if (!nameModalLearner.value) return
  const learnerId = nameModalLearner.value.id
  const name = nameModalValue.value.trim()
  if (!name) return
  savingName.value = true
  updatingNameId.value = learnerId
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/learners/${encodeURIComponent(learnerId)}/name`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ name })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || '更新に失敗しました')
      return
    }
    const idx = learners.value.findIndex((l) => l.id === learnerId)
    if (idx !== -1) learners.value[idx].name = data.name
    closeNameModal()
  } finally {
    savingName.value = false
    updatingNameId.value = null
  }
}

function openChannelModal (l) {
  channelModalLearner.value = l
  channelModalValue.value = l.channel || null
}

function closeChannelModal () {
  channelModalLearner.value = null
  channelModalValue.value = null
}

async function submitChannelChange () {
  if (!channelModalLearner.value) return
  const learnerId = channelModalLearner.value.id
  const channel = channelModalValue.value
  savingChannel.value = true
  updatingChannelId.value = learnerId
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/learners/${encodeURIComponent(learnerId)}/channel`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ channel })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || '更新に失敗しました')
      return
    }
    const idx = learners.value.findIndex((l) => l.id === learnerId)
    if (idx !== -1) learners.value[idx].channel = data.channel
    closeChannelModal()
  } finally {
    savingChannel.value = false
    updatingChannelId.value = null
  }
}

async function loadDefaultChannel () {
  loadingDefaultChannel.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/settings/default-channel`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (res.ok && data.channel) defaultChannel.value = data.channel
  } finally {
    loadingDefaultChannel.value = false
  }
}

async function submitDefaultChannelChange () {
  savingDefaultChannel.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/settings/default-channel`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ channel: defaultChannel.value })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || 'デフォルト種別の更新に失敗しました')
      await loadDefaultChannel()
    }
  } finally {
    savingDefaultChannel.value = false
  }
}

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function buildParams (offset = 0) {
  const params = new URLSearchParams()
  if (searchQ.value.trim()) params.set('q', searchQ.value.trim())
  params.set('limit', String(PAGE_SIZE))
  params.set('offset', String(offset))
  return params.toString()
}

const loadLearners = async () => {
  loading.value = true
  currentOffset.value = 0
  try {
    const token = await getToken()
    const qs = buildParams(0)
    const res = await fetch(`${apiBase}/api/manager/learners?${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      learners.value = []
      total.value = 0
      return
    }
    learners.value = data.learners || []
    total.value = data.total ?? 0
    currentOffset.value = learners.value.length
  } finally {
    loading.value = false
  }
}

const loadMore = async () => {
  const nextOffset = currentOffset.value
  if (nextOffset >= total.value) return
  loadingMore.value = true
  try {
    const token = await getToken()
    const qs = buildParams(nextOffset)
    const res = await fetch(`${apiBase}/api/manager/learners?${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return
    const list = data.learners || []
    learners.value = [...learners.value, ...list]
    currentOffset.value = nextOffset + list.length
  } finally {
    loadingMore.value = false
  }
}

async function grantBypassLearner (l) {
  if (!l?.id) return
  bypassGrantingId.value = l.id
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/grant-bypass-learner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ learner_id: l.id })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || 'bypass の付与に失敗しました')
      return
    }
    const idx = learners.value.findIndex((x) => x.id === l.id)
    if (idx !== -1) learners.value[idx].bypassSubscription = true
  } catch (e) {
    alert(e.message || 'エラーが発生しました')
  } finally {
    bypassGrantingId.value = null
  }
}

async function revokeBypassLearner (l) {
  if (!l?.id) return
  bypassRevokingId.value = l.id
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/revoke-bypass-learner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ learner_id: l.id })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || 'bypass の解除に失敗しました')
      return
    }
    const idx = learners.value.findIndex((x) => x.id === l.id)
    if (idx !== -1) learners.value[idx].bypassSubscription = false
  } catch (e) {
    alert(e.message || 'エラーが発生しました')
  } finally {
    bypassRevokingId.value = null
  }
}

onMounted(() => {
  loadLearners()
  loadDefaultChannel()
})
</script>

<style scoped>
.manager-learners { padding: 0; }

.page-title { margin: 0 0 0.5rem 0; font-size: 1.5rem; color: #1e293b; }
.page-desc { margin: 0 0 1.5rem 0; color: #64748b; font-size: 0.9rem; }
.manager-section { margin-bottom: 2rem; }
.section-title { margin: 0 0 0.75rem 0; font-size: 1.1rem; color: #334155; }

.card { background: white; border-radius: 8px; padding: 1.25rem; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
.filters { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 1rem; }
.filter-item { display: flex; flex-direction: column; gap: 0.25rem; }
.filter-label { font-size: 0.85rem; color: #64748b; font-weight: 500; }
.filter-search .form-input { min-width: 200px; }
.form-input { padding: 0.4rem 0.6rem; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 0.9rem; }
.toolbar { display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; }
.total-note { font-size: 0.9rem; color: #64748b; }
.btn { padding: 0.4rem 0.9rem; border-radius: 6px; font-size: 0.9rem; cursor: pointer; border: 1px solid transparent; }
.btn-primary { background: #3b82f6; color: white; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #e2e8f0; color: #334155; }
.btn-sm { padding: 0.25rem 0.5rem; font-size: 0.85rem; }
.loading-state, .empty-state { padding: 2rem; text-align: center; color: #64748b; }
.table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.data-table th, .data-table td { padding: 0.5rem 0.75rem; text-align: left; border-bottom: 1px solid #e2e8f0; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; }
.data-table th.num, .data-table td.num { text-align: right; }
.data-table th.action, .data-table td.action { white-space: nowrap; }
.btn-outline { background: transparent; border: 1px solid #94a3b8; color: #475569; }
.btn-outline:hover:not(:disabled) { background: #f1f5f9; }
.btn-primary { background: #3b82f6; color: white; border-color: #3b82f6; }
.btn-danger { background: #dc2626; color: white; border-color: #dc2626; }
.bypass-badge { display: inline-block; padding: 0.2rem 0.5rem; border-radius: 4px; font-size: 0.85rem; background: #fce7f3; color: #9d174d; }
.channel-badge { display: inline-block; padding: 0.2rem 0.5rem; border-radius: 4px; font-size: 0.85rem; white-space: nowrap; }
.channel-badge--self { background: #ede9fe; color: #5b21b6; }
.channel-badge--net-ad { background: #dbeafe; color: #1e40af; }
.channel-badge--kyoiku { background: #dcfce7; color: #166534; }
.channel-badge--default { background: #f1f5f9; color: #64748b; }
.load-more { margin-top: 1rem; text-align: center; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 100; }
.modal-card { background: white; border-radius: 8px; padding: 1.5rem; min-width: 320px; max-width: 90vw; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.modal-title { margin: 0 0 0.5rem 0; font-size: 1.2rem; color: #334155; }
.modal-desc { margin: 0 0 1rem 0; font-size: 0.9rem; color: #64748b; }
.modal-form { margin-bottom: 1rem; }
.form-label { display: flex; flex-direction: column; gap: 0.35rem; font-size: 0.9rem; color: #475569; }
.num-input { width: 6rem; }
.modal-actions { display: flex; justify-content: flex-end; gap: 0.75rem; }
</style>
